import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import AppMenu from '../components/AppMenu';
import OfflineStage from '../components/OfflineStage';
import PlayerBrowser from '../components/PlayerBrowser';
import PlayerDisplay from '../components/PlayerDisplay';
import PlayerSearch from '../components/PlayerSearch';
import ReleasePlayerModal from '../components/ReleasePlayerModal';
import SplitPane from '../components/SplitPane';
import TeamColumn from '../components/TeamColumn';
import Toasts from '../components/Toasts';
import {
    OfflineRuleError,
    STORAGE_KEY,
    initialState,
    offlineReducer,
} from '../domain/offlineAuction';
import { assignedPlayerIds, maxBid } from '../domain/team';
import usePersistentReducer, { usePersistentState } from '../hooks/usePersistentState';
import { fetchPlayers, indexPlayers } from '../lib/api';
import { downloadCsv, encodeTeams, parseTeams } from '../lib/teamsCsv';

// Asta offline: un solo operatore che assegna i giocatori a mano.
//
// Lo stato e un reducer puro persistito su localStorage (un refresh non
// cancella piu la sessione), le regole di spesa sono le stesse del server, e la
// select di assegnazione e controllata: prima conservava il partecipante
// precedente dopo un'assegnazione e la successiva "Assegna" premiava in
// silenzio la persona sbagliata.
//
// La schermata d'asta e' pensata per un proiettore, come la modalita
// spettatore dell'asta online: giocatore e prezzo a caratteri grandi, muro
// delle squadre sotto. Al contrario di quella pero' non e' in sola lettura —
// l'intestazione dell'applicazione resta e ogni comando e' al suo posto, solo
// dentro il tabellone invece che sotto.

// Le chiavi della sessione di lavoro: non l'asta (quella e' STORAGE_KEY) ma
// dove eravamo dentro l'asta. Chiudere la scheda per sbaglio non deve costare
// nemmeno la selezione in corso.
const VIEW_KEY = 'astafanta:offline:view';
const PLAYER_KEY = 'astafanta:offline:player';
const PARTICIPANT_KEY = 'astafanta:offline:participant';

export default function OfflineAuctionPage() {
    const [state, dispatch] = usePersistentReducer(offlineReducer, STORAGE_KEY, initialState);
    const [players, setPlayers] = useState([]);
    const [catalogue, setCatalogue] = useState({});
    const [quotazioniAvailable, setQuotazioniAvailable] = useState(true);
    const [messages, setMessages] = useState([]);

    // Del giocatore si salva l'id e non l'oggetto: il record buono e' quello
    // del listone appena scaricato, non una copia congelata in localStorage
    // che invecchia a ogni aggiornamento delle quotazioni.
    const [selectedPlayerId, setSelectedPlayerId] = usePersistentState(PLAYER_KEY, null);
    const [storedParticipant, setStoredParticipant] = usePersistentState(PARTICIPANT_KEY, '');
    // Il prezzo no: e' l'unica cosa che si sta ancora digitando, e ritrovare
    // una cifra vecchia sotto il bottone "Assegna" e' peggio che ridigitarla.
    const [price, setPrice] = useState('');
    const [newParticipant, setNewParticipant] = useState('');
    const [releaseTarget, setReleaseTarget] = useState(null);
    // Le due schermate, come nell'asta online: qui si assegna, di la si guarda
    // chi resta. Non serve il richiamo rosso all'asta in corso — offline non
    // c'e' nessun timer che scade mentre si consulta il listone.
    const [view, setView] = usePersistentState(VIEW_KEY, 'asta');
    const importRef = useRef(null);

    // Un partecipante rimosso o rinominato non deve restare selezionato: la
    // select tornerebbe muta e "Assegna" premierebbe un nome che non esiste.
    const selectedParticipant = state.participants.includes(storedParticipant)
        ? storedParticipant
        : '';
    const selectedPlayer = selectedPlayerId == null ? null : catalogue[selectedPlayerId] ?? null;

    const selectPlayer = useCallback(
        (player) => setSelectedPlayerId(player?.ID ?? null),
        [setSelectedPlayerId],
    );

    const notify = useCallback((text, level = 'error') => {
        setMessages((current) => [
            ...current.slice(-4),
            { id: `${Date.now()}-${Math.random()}`, text, level },
        ]);
    }, []);

    const dismiss = useCallback((id) => {
        setMessages((current) => current.filter((message) => message.id !== id));
    }, []);

    const run = useCallback(
        (action) => {
            try {
                dispatch(action);
                return true;
            } catch (error) {
                if (error instanceof OfflineRuleError) {
                    notify(error.message);
                    return false;
                }
                throw error;
            }
        },
        [dispatch, notify],
    );

    useEffect(() => {
        fetchPlayers()
            .then((payload) => {
                setPlayers(payload.players);
                setCatalogue(indexPlayers(payload.players));
                setQuotazioniAvailable(payload.quotazioni_available);
                if (payload.stale) {
                    notify('Listone non aggiornato: sto usando la copia locale.', 'info');
                }
            })
            .catch((error) => notify(error.message));
    }, [notify]);

    const excluded = useMemo(() => assignedPlayerIds(state.teams), [state.teams]);

    const assign = () => {
        const parsed = Number.parseInt(price, 10);
        if (!selectedPlayer) return notify('Seleziona un giocatore.');
        if (!selectedParticipant) return notify('Seleziona un partecipante.');
        if (!Number.isInteger(parsed) || parsed < 1) {
            return notify('Il prezzo deve essere un numero intero maggiore di zero.');
        }
        const ok = run({
            type: 'assign_player',
            participant: selectedParticipant,
            player: selectedPlayer,
            price: parsed,
        });
        if (ok) {
            selectPlayer(null);
            setPrice('');
        }
        return ok;
    };

    const handleExport = () => {
        const csv = encodeTeams(state.teams, catalogue);
        if (!csv) return notify('Nessuna squadra da esportare.', 'info');
        return downloadCsv(csv);
    };

    const handleImport = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (loaded) => {
            const teams = parseTeams(String(loaded.target.result), catalogue);
            run({ type: 'import_teams', teams });
        };
        reader.readAsText(file);
    };

    // Le stesse azioni due volte: in riga nell'header sui grandi schermi,
    // dentro il menu a comparsa sul telefono. Prima erano semplicemente
    // nascoste sotto i 768px.
    const actions = (
        <>
            <Link to="/" className="btn btn--ghost btn--sm">
                Home
            </Link>
            <Link to="/asta-online" className="btn btn--ghost btn--sm">
                Asta online
            </Link>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleExport}>
                Esporta squadre
            </button>
            <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => importRef.current?.click()}
            >
                Importa squadre
            </button>
            <button
                type="button"
                className="btn btn--stop btn--sm"
                onClick={() => {
                    if (window.confirm('Azzerare l’asta offline? I dati salvati andranno persi.')) {
                        dispatch({ type: 'reset' });
                        selectPlayer(null);
                        setStoredParticipant('');
                        setPrice('');
                    }
                }}
            >
                Nuova asta
            </button>
        </>
    );

    const currentTeam = selectedParticipant ? state.teams[selectedParticipant] : undefined;
    const ceiling = currentTeam ? maxBid(currentTeam, state.settings.players_per_team) : 0;

    return (
        <div className="app">
            <header className="app-header">
                <div className="app-brand">
                    <img src="/logos/seriea.png" alt="Serie A" />
                    <h1 className="u-truncate">Asta Offline 📵</h1>
                </div>
                <div className="app-actions">{actions}</div>
                <AppMenu>{actions}</AppMenu>
            </header>

            <input
                type="file"
                ref={importRef}
                accept=".csv,text/csv"
                className="u-visually-hidden"
                onChange={handleImport}
                aria-label="Importa squadre da file CSV"
            />

            <main className={`app-main${view === 'asta' ? ' app-main--stage' : ''}`}>
                <div className="btn-group view-switch" role="group" aria-label="Schermata">
                    <button
                        type="button"
                        className={`btn btn--sm ${view === 'asta' ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => setView('asta')}
                    >
                        Asta
                    </button>
                    <button
                        type="button"
                        className={`btn btn--sm ${view === 'listone' ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => setView('listone')}
                    >
                        Listone
                    </button>
                </div>

                {view === 'listone' ? (
                    <SplitPane
                        storageKey="astafanta:split:offline"
                        left={
                            <PlayerBrowser
                                players={players}
                                excludedIds={excluded}
                                selectedId={selectedPlayer?.ID}
                                onSelect={selectPlayer}
                            />
                        }
                        right={<PlayerDisplay player={selectedPlayer} />}
                    />
                ) : (
                    <>
                        {/* I comandi dell'operatore stanno sopra il tabellone e
                            restano a corpo normale: li legge chi batte l'asta,
                            a un braccio di distanza, non la stanza. */}
                        <div className="offline-controls">
                            <PlayerSearch
                                players={players}
                                excludedIds={excluded}
                                onSelect={selectPlayer}
                                team={currentTeam}
                                playersPerTeam={state.settings.players_per_team}
                            />
                            <div className="panel">
                                <div className="panel-header">
                                    <h2 className="panel-title">Partecipanti</h2>
                                </div>
                                <div className="panel-body">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Nome del partecipante"
                                            aria-label="Nome del partecipante"
                                            value={newParticipant}
                                            onChange={(event) => setNewParticipant(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' && newParticipant.trim()) {
                                                    if (run({ type: 'add_participant', name: newParticipant })) {
                                                        setNewParticipant('');
                                                    }
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn--primary"
                                            onClick={() => {
                                                if (run({ type: 'add_participant', name: newParticipant })) {
                                                    setNewParticipant('');
                                                }
                                            }}
                                        >
                                            Aggiungi
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <OfflineStage
                            player={selectedPlayer}
                            participants={state.participants}
                            participant={selectedParticipant}
                            onParticipant={setStoredParticipant}
                            price={price}
                            onPrice={setPrice}
                            onAssign={assign}
                            team={currentTeam}
                            ceiling={ceiling}
                            playersPerTeam={state.settings.players_per_team}
                        />

                        <section aria-label="Rose">
                            {state.participants.length === 0 ? (
                                <p className="u-dim">Aggiungi almeno un partecipante per iniziare.</p>
                            ) : (
                                <div className="roster-grid roster-grid--stage">
                                    {state.participants.map((name) => (
                                        <TeamColumn
                                            key={name}
                                            name={name}
                                            teamName={state.teams[name]?.imported_name}
                                            team={state.teams[name]}
                                            catalogue={catalogue}
                                            playersPerTeam={state.settings.players_per_team}
                                            isCurrentTurn={name === selectedParticipant}
                                            onRename={(oldName, newName) => {
                                                if (run({ type: 'rename_participant', oldName, newName })) {
                                                    // La selezione segue il nome nuovo: senza,
                                                    // rinominare chi stava per comprare la
                                                    // azzerava.
                                                    if (storedParticipant === oldName) {
                                                        setStoredParticipant(newName.trim());
                                                    }
                                                }
                                            }}
                                            onRemoveParticipant={(participant) =>
                                                run({ type: 'remove_participant', name: participant })
                                            }
                                            onRemovePlayer={(participant, entry) =>
                                                run({
                                                    type: 'remove_player',
                                                    participant,
                                                    playerId: entry.player_id,
                                                })
                                            }
                                            onReleasePlayer={(participant, entry) =>
                                                setReleaseTarget({ participant, entry })
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </main>

            <ReleasePlayerModal
                show={Boolean(releaseTarget)}
                entry={releaseTarget?.entry}
                player={catalogue[releaseTarget?.entry?.player_id]}
                quotazioniAvailable={quotazioniAvailable}
                onConfirm={() => {
                    run({
                        type: 'release_player',
                        participant: releaseTarget.participant,
                        playerId: releaseTarget.entry.player_id,
                        inSerieA: catalogue[releaseTarget.entry.player_id]?.in_serie_a ?? null,
                    });
                    setReleaseTarget(null);
                }}
                onClose={() => setReleaseTarget(null)}
            />

            <Toasts messages={messages} onDismiss={dismiss} />
        </div>
    );
}
